import { useContext, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Header, Popup, Ref } from "semantic-ui-react";
import { ACTIONS, QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";

import orderActions from "actions/order";
import { Notification, STATUS_TYPES, USER_PERMISSIONS, PRODUCT_PERMISSIONS } from "dyl-components";
import ConvertNewCustomerPopup from "shared/ConvertNewCustomerPopup";
import { formatOrder } from "shared/schemas/cart/cartSchema";
import quoteActions from 'actions/quote';
import ConvertToNewCustomerForm from "shared/forms/ConvertToNewCustomerForm";
import ConvertAccountToCustomerForm from "shared/forms/ConvertAccountToCustomerForm";
import RestrictedView from "shared/RestrictedView";
import accountActions from "actions/account";
import { PhoneUtil } from "utils";
import contactsActions from "actions/contacts";
import { useParams } from "react-router-dom";

const ConvertToCustomerOrder = ({
    account_id,
    contact_id,
    account_primary_contact_id,
    isLinkedToCustomer,
    disabled,
    isAllowedToUpsell,
    updateQuote
}) => {
    const {
        formState: { isValid },
        handleSubmit,
    } = useFormContext();

    const isSaving = useSelector(
        (state) => state.quote.isCreatingVersion || state.order.isCreating || state.quote.opportunityBeingUpdated
    );
    const isDialerRunning = useSelector(state => PhoneUtil.isDialerRunning(state.officeView?.dialer));

    const quote = useSelector(state => state.quote.quote);
    const opportunity_id = quote?.opportunity_id;

    const [conversionModalOpen, setConversionModalOpen] = useState(false);
    const [isConversionFormModified, setIsConversionFormModified] = useState(false);

    const onCloseConversionForm = () => {
        setConversionModalOpen("");
    };

    const { onRefreshAccountInformation, onViewOrder, quoteBuilderConfig } =
        useContext(QuoteBuilderContext);
    const { id: quote_id, action } = quoteBuilderConfig;

    const dispatch = useDispatch();

    const onCreateOrder = async (data) => {
        try {
            await updateQuote(data);
            const order_id = await dispatch(
                orderActions.create(
                    {
                        items: formatOrder(data.cart),
                    },
                    {
                        quote_id,
                        opportunity_id,
                        account_id,
                    }
                )
            );
            Notification.alert(
                "Successfully created order",
                STATUS_TYPES.SUCCESS
            );
            await onRefreshAccountInformation();
            onViewOrder(order_id, account_id);
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to create order", STATUS_TYPES.ERROR);
        }
    };

    const onCreateOrderForUpdatedAccount = (account_id, shouldMoveQuotes) => {
        return handleSubmit(async (data) => {
            try {
                await updateQuote(data);
                if (shouldMoveQuotes) {
                    await dispatch(quoteActions.updateQuoteAccountId(opportunity_id, { account_id }));
                }
                const order_id = await dispatch(
                    orderActions.create(
                        {
                            items: formatOrder(data.cart),
                        },
                        {
                            quote_id,
                            account_id,
                            opportunity_id
                        }
                    )
                );
                Notification.alert(
                    "Successfully created order",
                    STATUS_TYPES.SUCCESS
                );
                onViewOrder(order_id, account_id);
            } catch (e) {
                console.log(e);
                Notification.alert(
                    "Failed to create order",
                    STATUS_TYPES.ERROR
                );
            }
        })();
    };

    const modalRef = useRef();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const isPrimaryContact =
        account_primary_contact_id && Number(account_primary_contact_id) === Number(contact_id);
    
    const { contact_id: profileContactId } = useParams();

    const createOrderAfterRefreshingAccountInfo = async (account_id, shouldMoveQuotes) => {
        if (isDialerRunning && profileContactId) {
            dispatch(contactsActions.dialerViewLookup(profileContactId));
        }
        await onRefreshAccountInformation();
        return onCreateOrderForUpdatedAccount(account_id, shouldMoveQuotes);
    };

    const { control } = useFormContext();

    const { field: contactAddressField } = useController({
        name: "contact_address",
        control,
    });

    const { field: contactEmailField } = useController({
        name: "contact_email",
        control,
    });

    const { field: contactPhoneField } = useController({
        name: "contact_phone",
        control,
    });

    const { field: contactEmailTypeField } = useController({
        name: "contact_email_type",
        control,
    });

    const { field: contactPhoneTypeField } = useController({
        name: "contact_phone_type",
        control,
    });

    if (isLinkedToCustomer) {
        if (action === ACTIONS.UPSELL) {
            return (
                <Popup
                    open={isDropdownOpen && !isAllowedToUpsell}
                    disabled={
                        isAllowedToUpsell
                    }
                    position={'left center'}
                    content={"Current products do not match existing subscription"}
                    inverted
                    trigger={(
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_ADD} userRestrict>
                            <Button disabled={!isAllowedToUpsell} basic color='blue' onClick={handleSubmit(onCreateOrder)}>
                                Create Order
                            </Button> 
                        </RestrictedView>
                    )}
                />
            )
        }
        const convertNewCustomerPoup = (
            <Popup
                content={
                    <div>
                        <Header color="primary">
                            Create Customer-Account
                        </Header>
                        <ConvertToNewCustomerForm 
                            size="small"
                            owner_id={contact_id}
                            onClose={onCloseConversionForm}
                            refresh={createOrderAfterRefreshingAccountInfo}
                            onModify={(isModified) => { setIsConversionFormModified(isModified) }}
                            pipeline_id={opportunity_id}
                            existing_account_id={account_id}
                            account_details={{
                                address: contactAddressField.value,
                                email: contactEmailField.value,
                                email_type: contactEmailTypeField.value,
                                phone: contactPhoneField.value,
                                phone_type: contactPhoneTypeField.value
                            }}
                        />
                    </div>
                }
                open={conversionModalOpen === "new-customer"}
                position="bottom center"
                context={modalRef}
                onClose={onCloseConversionForm}
                closeOnDocumentClick={!isConversionFormModified}
            />
        )
        if (action === ACTIONS.CONVERT_NEW_CUSTOMER) {
            return (
                <>
                    {convertNewCustomerPoup}
                    <Ref innerRef={modalRef}>
                        <ConvertNewCustomerPopup
                            position="left center"
                            open={
                                isDropdownOpen &&
                                (isPrimaryContact)
                            }
                            isPrimaryContact={isPrimaryContact}
                            account_id={account_id}
                            trigger={
                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_ADD} productPermission={PRODUCT_PERMISSIONS.RECORDS} userRestrict>
                                    <Button 
                                        disabled={isPrimaryContact} 
                                        color='blue' 
                                        onClick={() => {
                                            setConversionModalOpen(
                                                "new-customer"
                                            );
                                        }}
                                        basic
                                    >
                                        Create Order 
                                    </Button>
                                </RestrictedView>
                            }
                        />
                    </Ref>
                </>
            )
        }
        return (
            <>
                {convertNewCustomerPoup}
                <Ref innerRef={modalRef}>
                    <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ORDERS_ADD} userRestrict>
                        <Dropdown
                            as={Button}
                            onClose={() => {
                                setIsDropdownOpen(false);
                            }}
                            onOpen={() => {
                                setIsDropdownOpen(true);
                            }}
                            color="blue"
                            text="Create Order"
                            basic
                            disabled={!isValid || isSaving || disabled}
                            icon={null}
                            pointing="bottom"
                        >
                            <Dropdown.Menu>
                                <Popup
                                    open={isDropdownOpen && !isAllowedToUpsell}
                                    disabled={
                                        isAllowedToUpsell
                                    }
                                    position={'left center'}
                                    content={"Current products do not match existing subscription"}
                                    inverted
                                    trigger={(
                                        <Dropdown.Item
                                            onClick={handleSubmit(onCreateOrder)}
                                            disabled={!isAllowedToUpsell}
                                        >
                                            {!quote.is_for_reactivation ? "Upsell Existing Customer" : "Reactivate Customer"}
                                        </Dropdown.Item>
                                    )}
                                />
                                <ConvertNewCustomerPopup
                                    position="right center"
                                    open={
                                        isDropdownOpen &&
                                        (isPrimaryContact)
                                    }
                                    isPrimaryContact={isPrimaryContact}
                                    account_id={account_id}
                                    trigger={
                                        <Dropdown.Item
                                            disabled={
                                                isPrimaryContact
                                            }
                                            onClick={() => {
                                                setConversionModalOpen(
                                                    "new-customer"
                                                );
                                            }}
                                        >
                                            Convert New Customer
                                        </Dropdown.Item>
                                    }
                                />
                            </Dropdown.Menu>
                        </Dropdown>
                    </RestrictedView>
                </Ref>
            </>
        );
    }

    const commonProperties = {
        size: 'small',
        owner_id: contact_id ? Number(contact_id) : null,
        refresh: (account_id) => {
            return createOrderAfterRefreshingAccountInfo(account_id, true)
        },
        pipeline_id: opportunity_id,
        onModify: (isModified) => { setIsConversionFormModified(isModified) },
        onClose: onCloseConversionForm
    }

    return (
        <RestrictedView type="popupDisabled" userPermission={USER_PERMISSIONS.ORDERS_ADD} productPermission={!account_id ? PRODUCT_PERMISSIONS.RECORDS : false} userRestrict>
            <Popup
                trigger={
                        <Button
                            disabled={!isValid || isSaving || disabled}
                            basic
                            type="button"
                            color="primary"
                        > 
                            Create Order
                        </Button> 
                }
                open={conversionModalOpen === "customer" || conversionModalOpen === "new-customer"}
                content={
                    <>
                        <Header color="primary">{account_id ? "Convert to Customer" : "Create Customer-Account"}</Header>
                        {account_id ? (
                            <ConvertAccountToCustomerForm 
                                {...commonProperties}
                                existing_account_id={account_id}
                                current_primary_contact_id={account_primary_contact_id}
                            />
                        ) : (
                            <ConvertToNewCustomerForm 
                                {...commonProperties}
                                account_details={{
                                    address: contactAddressField.value,
                                    email: contactEmailField.value,
                                    email_type: contactEmailTypeField.value,
                                    phone: contactPhoneField.value,
                                    phone_type: contactPhoneTypeField.value
                                }}
                            />
                        )}
                    </>
                }
                onOpen={() => {
                    setConversionModalOpen(account_id ? "customer" : "new-customer");
                    if (account_id) {
                        dispatch(accountActions.readContactsForPinning(account_id, { page: 1, limit: 100 }));
                    }
                }}
                on="click"
                onClose={onCloseConversionForm}
                position="top center"
                closeOnDocumentClick={!isConversionFormModified}
            />
        </RestrictedView>
    );
};

export default ConvertToCustomerOrder;
